// Shared hooks for the Nurse Dashboard.
import { useEffect, useMemo, useState } from "react";

import useBedCensusData from "../ipd/hooks/useBedCensusData";
import { getMyRoster, getMyAssignments, getServiceFulfillmentTypes, getShiftScheduleAt, listOf } from "./api/nurseApi";
import { currentShift, msUntilNextShiftChange, DEFAULT_SCHEDULE } from "./shiftWindow";

const WORKING_STATUSES = new Set(["on_duty", "overtime"]);

const bedCodeOf = (b) => b.code || b.bed_id || b.bed_code || (b.id != null ? String(b.id) : "");

// The ward codes a nurse is rostered to work for one specific (rosterDate,
// shift). Exported for testing. These were "dept::ward" pairs until the
// bed/department decoupling; a ward is a room category alone now, and neither
// roster rows nor beds carry a department to pair it with.
export function rosterWardKeys(rows, rosterDate, shift) {
  const set = new Set();
  rows.forEach((row) => {
    if (row.roster_date !== rosterDate) return;
    if (row.shift !== shift) return;
    if (!WORKING_STATUSES.has(row.status)) return;
    set.add(row.ward_code);
  });
  return set;
}

// The facility's shift clock. Every screen that reasons about shifts needs it
// and none of them can block on it — so this returns DEFAULT_SCHEDULE until the
// real one arrives, and keeps it if the fetch fails. A config outage must not
// blank out a nurse's patient list; the built-in default is the same clock the
// module used when it was hardcoded.
export function useShiftSchedule(onDate) {
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const date = onDate || currentShift(new Date(), DEFAULT_SCHEDULE).rosterDate;
    getShiftScheduleAt(date)
      .then((r) => {
        if (!alive) return;
        const shifts = r?.data?.shifts;
        if (Array.isArray(shifts) && shifts.length) setSchedule(shifts);
      })
      .catch(() => { /* keep the default */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [onDate]);
  return { schedule, loading };
}

// The set of bed codes a nurse is responsible for THIS SHIFT — the single
// definition of "my patients". Coverage is the roster (every bed in a ward the
// nurse is rostered to for the shift in progress) UNION any beds individually
// pinned to them for that shift. Both halves are now time-bounded: the roster
// by (roster_date, shift), the pins by the backend's in-force gate on mine=1.
//
// The shift in progress is resolved in IST, never from the workstation clock,
// and is re-resolved when it changes — a ward PC left open across a shift
// change or across midnight used to keep the coverage it had at page load
// forever. Returns { myBedCodes: Set|null (null = loading) }.
export function useMyCoverage() {
  const { beds } = useBedCensusData();
  const [rosterWards, setRosterWards] = useState(null); // Set of "dept::wardCode"
  const [assignedBeds, setAssignedBeds] = useState(null); // Set of bed_code
  const { schedule } = useShiftSchedule();
  const [{ rosterDate, shift }, setWindow] = useState(() => currentShift(new Date(), DEFAULT_SCHEDULE));

  // Re-resolve when the schedule loads (the first answer used the default) and
  // again at each boundary of the schedule actually in force.
  useEffect(() => {
    setWindow(currentShift(new Date(), schedule));
    const t = setTimeout(
      () => setWindow(currentShift(new Date(), schedule)),
      msUntilNextShiftChange(new Date(), schedule) + 1000
    );
    return () => clearTimeout(t);
  }, [schedule, rosterDate, shift]);

  useEffect(() => {
    let alive = true;
    getMyRoster({ from_date: rosterDate, to_date: rosterDate })
      .then((r) => { if (alive) setRosterWards(rosterWardKeys(listOf(r), rosterDate, shift)); })
      .catch(() => { if (alive) setRosterWards(new Set()); });
    // No date param: nurse_bed_assignment/?mine=1 is gated server-side to the
    // pins in force for the shift in progress.
    getMyAssignments()
      .then((r) => { if (alive) setAssignedBeds(new Set(listOf(r).map((a) => a.bed_code).filter(Boolean))); })
      .catch(() => { if (alive) setAssignedBeds(new Set()); });
    return () => { alive = false; };
  }, [rosterDate, shift]);

  const myBedCodes = useMemo(() => {
    if (rosterWards === null || assignedBeds === null) return null; // still loading
    const set = new Set(assignedBeds);
    beds.forEach((b) => {
      const rcc = b.room_category_code;
      if (!rcc) return;
      if (rosterWards.has(rcc)) {
        set.add(bedCodeOf(b));
      }
    });
    return set;
  }, [beds, rosterWards, assignedBeds]);

  return { myBedCodes, loading: myBedCodes === null };
}

// The admission ids the nurse covers — covered beds mapped through the census
// (`patients`) to their admissions. Returns null while coverage is still
// loading, so callers can hold "no data" rather than flash empty. Pure helper
// (not a hook) so any view can derive its scope from useMyCoverage + context.
export function coveredAdmissionIds(patients, myBedCodes) {
  if (!myBedCodes) return null;
  return new Set(
    patients.filter((p) => p.bed && myBedCodes.has(p.bed)).map((p) => String(p.admissionId))
  );
}

// A task is actionable (tappable to open the fulfillment dialog) only while
// it isn't already completed or cancelled. Cancelled in particular must never
// be tappable: fulfill_care_plan_task/ rejects start/complete on a cancelled
// task (its order was stopped), so leaving it tappable just walks the nurse
// into a guaranteed backend error instead of showing the true state up front.
// Shared by MyTasks and PatientTabs' Tasks tab so the rule lives in one place.
export function isTaskActionable(t) {
  const s = String(t.status).toLowerCase();
  return s !== "completed" && s !== "cancelled";
}

// The fulfillment form library as { [id]: name } — "Wound Dressing",
// "Medication Administration". A task carries only fulfillment_type_id, and
// nurse_dashboard_tasks/ inlines the form's STRUCTURE but never its NAME, so
// the name has to be resolved here. It is what names a task whose care plan
// carries no description of its own (DEFIPD082). Fetched per consumer, the
// same way useMyCoverage fetches its roster.
export function useFulfillmentNames() {
  const [names, setNames] = useState({});
  useEffect(() => {
    let alive = true;
    getServiceFulfillmentTypes()
      .then((r) => {
        if (!alive) return;
        const map = {};
        listOf(r).forEach((t) => {
          if (t?.id != null && t?.name) map[t.id] = t.name;
        });
        setNames(map);
      })
      .catch(() => { if (alive) setNames({}); });
    return () => { alive = false; };
  }, []);
  return names;
}

// The input stays immediate (bind the TextField to the raw state); consumers
// filter/fetch on the returned value, which settles `delay` ms after the last
// keystroke. Used by every patient/name search box in the nurse + manager
// sections so a fast typist doesn't re-filter (or re-fetch) per keystroke.
export function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
